import IndicatorScreen from 'components/indicator/IndicatorScreen';
import { AppConst } from 'constants/appConst';
import styles from './ModalCompanyImageGallery.module.scss';

import { type JSX } from 'react';
import ReactModal from 'react-modal';
import { useAtomValue } from 'jotai';
import { AcctState } from 'stores/atoms/acctState';
import { useAppState } from 'hooks/useAppState';
import {
  formatBytes,
  formatRateToPercent,
  formatTimeToDate,
  getContentImageUrl,
  getImageType,
} from 'utils/app';
import { useCompanyImageGallery } from './useCompanyImageGallery';

/**
 * カンパニー画像ギャラリーモーダル
 */
export default function ModalCompanyImageGallery(props: {
  setOpen: (open: boolean) => void;
  open: boolean;
  onImageSelected: (imageKey: string) => void;
}): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const acctState = useAtomValue(AcctState);
  const { isAiImageCountNoLimit } = useAppState();

  const {
    initialized,
    processing,
    keywordState,
    sortState,
    selectedImageState,
    companyImagesState,
    dropdownOpen,
    uploadErrorState,
    isDragging,
    tab,
    aiPrompt,
    aiLoading,
    inputFileRef,
    dropdownRef,
    setTab,
    setAiPrompt,
    setKeywordState,
    onAfterOpen,
    handleClose,
    handleOpenFileUploader,
    handleSelectImage,
    handleImgFileInput,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDelete,
    handleSubmit,
    handleToggleDropdown,
    handleSort,
    handleAiGenerate,
    activePlan,
    isAiImageCountLimit,
  } = useCompanyImageGallery(props);

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {/* ローディング中の表示 */}
      <IndicatorScreen visible={initialized || processing || aiLoading} />
      <ReactModal
        isOpen={props.open}
        onAfterOpen={onAfterOpen}
        onRequestClose={handleClose}
        bodyOpenClassName="ReactModal__body--open"
        style={{
          overlay: {
            zIndex: 2000,
            backgroundColor: 'rgba(30, 30, 30,0.5)',
          },
          content: {
            maxWidth: '830px',
            maxHeight: 'calc(100vh - 40px)',
            overflowY: 'auto',
          },
        }}
        className="ReactModal__Content__Custom"
        ariaHideApp={false}
      >
        <div className="modal modal-content rounded-4 modal-media">
          <div className="modal-header">
            <h4 className="modal-title">画像ギャラリー</h4>
          </div>
          <div className="modal-body">
            {/* タブ切り替え */}
            <div className="d-flex mb-3">
              <button
                className={`btn ${styles.btnTab} me-2 ${tab === 'ai' ? styles.btnTabActive : ''}`}
                onClick={() => setTab('ai')}
                type="button"
              >
                AIで画像生成
              </button>
              <button
                className={`btn ${styles.btnTab} ${tab === 'upload' ? styles.btnTabActive : ''}`}
                onClick={() => setTab('upload')}
                type="button"
              >
                アップロード
              </button>
            </div>

            {/* タブ内容 */}
            {tab === 'ai' && (
              <div>
                <div className="form-control-container mb-3">
                  <div className="d-flex">
                    <div className="flex-grow-1  me-2">
                      <textarea
                        className="form-control"
                        placeholder="生成したい画像の説明を入力"
                        value={aiPrompt}
                        onChange={e => setAiPrompt(e.target.value)}
                        disabled={aiLoading}
                      />
                    </div>
                    {/* 右側の列：ボタンとリンクを縦に配置 */}
                    <div className="d-flex flex-column">
                      <button
                        className="btn btn-common ai-generate"
                        style={{
                          width: isAiImageCountLimit ? 'auto' : '100px',
                        }}
                        onClick={handleAiGenerate}
                        disabled={aiLoading || !aiPrompt.trim() || isAiImageCountLimit}
                      >
                        {isAiImageCountLimit
                          ? '上限に達しました'
                          : aiLoading
                            ? '生成中...'
                            : 'AIで生成'}
                      </button>
                      {isAiImageCountLimit && (
                        <a
                          className="detail hover d-flex align-items-center justify-content-center mt-2"
                          target="_blank"
                          href={AppConst.url.upgrade}
                        >
                          <img
                            src="/img/icon/exclamation.svg"
                            width={18}
                            className="me-1"
                            style={{ verticalAlign: 'text-top' }}
                          />
                          プランアップデート
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="form-label d-flex justify-content-between">
                    <span>画像生成数</span>
                    <span>
                      {acctState.company.aiImageUsageCount} /{' '}
                      {isAiImageCountNoLimit() ? '無制限' : (activePlan?.aiImageCountLimit ?? 0)}
                    </span>
                  </div>
                  <div
                    className="progress"
                    role="progressbar"
                    aria-valuenow={25}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    <div
                      className="progress-bar"
                      style={{
                        width: `${
                          isAiImageCountNoLimit()
                            ? 0
                            : formatRateToPercent(
                                acctState.company.aiImageUsageCount,
                                activePlan?.aiImageCountLimit ?? 0
                              )
                        }%`,
                      }}
                    ></div>
                  </div>
                </div>
                <div className="form-label">
                  <span>
                    ※{formatTimeToDate(acctState.company.usageCountClearAt, 'yyyy/MM/dd')}
                    にリセットされます
                  </span>
                </div>
              </div>
            )}
            {tab === 'upload' && (
              <div
                style={{
                  marginBottom: '30px',
                  paddingBottom: '0',
                  borderBottom: '1px solid #ddd',
                }}
              >
                <input
                  type="file"
                  accept={AppConst.imageTypes.join(',')}
                  multiple
                  ref={inputFileRef}
                  onChange={handleImgFileInput}
                  style={{ display: 'none' }}
                />
                <div className="form-control-container mb-3">
                  <div
                    id="button-upload"
                    className="form-control"
                    onClick={!processing ? handleOpenFileUploader : undefined}
                    onDragOver={!processing ? handleDragOver : undefined}
                    onDragLeave={!processing ? handleDragLeave : undefined}
                    onDrop={!processing ? handleDrop : undefined}
                    style={{
                      cursor: processing ? 'not-allowed' : 'pointer',
                      minHeight: '100px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#999',
                      opacity: processing ? 0.5 : 1,
                      pointerEvents: processing ? 'none' : 'auto',
                      ...(isDragging && {
                        border: '2px dashed #2b6cb0',
                        backgroundColor: '#ebf4ff',
                      }),
                    }}
                  >
                    ファイルを選択またはドラッグ&ドロップ
                  </div>
                </div>
                {uploadErrorState && (
                  <div className="mt-2 text-danger small" style={{ whiteSpace: 'pre-wrap' }}>
                    {uploadErrorState}
                  </div>
                )}
              </div>
            )}

            <div className="d-flex d-flex-search">
              <div className="flex-shrink-0 flex-left">
                <div className="dropdown" ref={dropdownRef}>
                  <button
                    className="btn btn-dropdown-media dropdown-toggle"
                    type="button"
                    id="dropdown-media"
                    onClick={handleToggleDropdown}
                    aria-expanded={dropdownOpen}
                    style={{ width: '140px', minWidth: '140px' }}
                  >
                    {sortState === 'newest'
                      ? '最新順'
                      : sortState === 'oldest'
                        ? '古い順'
                        : 'ファイル名順'}
                  </button>
                  <ul
                    className={`dropdown-menu ${dropdownOpen ? 'show' : ''}`}
                    aria-labelledby="dropdown-media"
                  >
                    <li>
                      <button
                        onClick={() => handleSort('newest')}
                        className="dropdown-item"
                        type="button"
                      >
                        最新順
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => handleSort('oldest')}
                        className="dropdown-item"
                        type="button"
                      >
                        古い順
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => handleSort('file')}
                        className="dropdown-item"
                        type="button"
                      >
                        ファイル名順
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="flex-grow-1 flex-right">
                <input
                  type="search"
                  id="search-media"
                  name="search-media"
                  placeholder="ファイル名検索"
                  onChange={e => setKeywordState(e.target.value)}
                />
              </div>
            </div>

            <div className="d-md-flex d-flex-card">
              <div className="flex-shrink-0 flex-item-left company-image-garrely">
                <div className="row row-0">
                  {companyImagesState.map(image => {
                    return (
                      // keywordStateに値が入っている場合、ファイル名にkeywordStateが含まれる画像のみ表示
                      (keywordState == '' || image.name.includes(keywordState)) && (
                        <div
                          key={`modal-company-image-modal-${image.id}`}
                          className={'col-6 col-md-4'}
                        >
                          <a
                            onClick={() => handleSelectImage(image.id)}
                            className={`card card-media ${
                              selectedImageState?.id === image.id ? 'active' : ''
                            }`}
                          >
                            <div className="card-body">
                              <div className="img-container">
                                <img
                                  src={getContentImageUrl(image.imageKey)}
                                  className="img-fluid d-block mx-auto mCS_img_loaded"
                                />
                              </div>

                              <h5 className="card-title">{image.name}</h5>

                              <dl className="dl-list-indent">
                                <dt>{getImageType(image.name)}</dt>

                                <dd>{formatTimeToDate(image.createdAt, 'yyyy/MM/dd')}</dd>
                              </dl>
                            </div>
                          </a>
                        </div>
                      )
                    );
                  })}
                </div>
              </div>
              <div className="flex-grow-1 flex-item-right">
                <div className="card card-media-lg">
                  {selectedImageState && (
                    <div className="card-body">
                      <div className="card-body-top">
                        <img
                          src={getContentImageUrl(selectedImageState.imageKey)}
                          className="img-fluid d-block mx-auto"
                        />
                        <h5 className="card-title">{selectedImageState.name}</h5>
                        <ul className="list-unstyled">
                          <li>
                            ファイル形式：
                            {getImageType(selectedImageState.name)}
                          </li>

                          <li>
                            登録日時：
                            {formatTimeToDate(selectedImageState.createdAt, 'yyyy/MM/dd HH:mm')}
                          </li>

                          <li>
                            サイズ：
                            {`${selectedImageState.imageWidth}px×${selectedImageState.imageHeight}px`}
                            {`（${formatBytes(selectedImageState.imageSize)}）`}
                          </li>
                        </ul>
                      </div>

                      <div className="card-body-bottom">
                        <div className="d-flex justify-content-between align-items-center">
                          <a
                            href={getContentImageUrl(selectedImageState.imageKey)}
                            className="btn btn-card gray"
                          >
                            ダウンロード
                          </a>

                          <a onClick={handleDelete} className="btn btn-delete p-0 hover-img">
                            <img src="/img/icon/trash-can.svg" alt="" />
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button onClick={handleClose} type="button" className="btn btn-card btn-card-lg gray">
              キャンセル
            </button>
            <button
              onClick={handleSubmit}
              type="button"
              className="btn btn-card btn-card-lg moderate-blue"
              disabled={!selectedImageState}
            >
              この画像に決定
            </button>
          </div>
        </div>
      </ReactModal>
    </>
  );
}
